import { $inputRule, $remark } from '@milkdown/kit/utils';
import { InputRule } from '@milkdown/kit/prose/inputrules';
import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import { visit } from 'unist-util-visit';
import type { Node } from 'unist';

/**
 * Smart text replacement configuration
 */
interface SmartTextConfig {
  /** Enable arrow replacement: -> to right arrow */
  arrows?: boolean;
  /** Enable fraction replacements: 1/2 to one half, 1/4 to one quarter, 3/4 to three quarters */
  fractions?: boolean;
  /** Enable symbol replacements: (c) to copyright, (r) to registered, (tm) to trademark */
  symbols?: boolean;
  /** Enable ellipsis replacement: ... to horizontal ellipsis */
  ellipsis?: boolean;
  /** Enable smart quotes: straight quotes to curly quotes */
  smartQuotes?: boolean;
  /** Enable em dash: -- to em dash */
  emDash?: boolean;
  /** Enable en dash: number-number to number en-dash number */
  enDash?: boolean;
}

/**
 * Default configuration - all features enabled
 */
const defaultSmartTextConfig: SmartTextConfig = {
  arrows: true,
  fractions: true,
  symbols: true,
  ellipsis: true,
  smartQuotes: true,
  emDash: true,
  enDash: true,
};

function createRemarkReplacementPlugin(enabled: {
  arrows: boolean;
  fractions: boolean;
  symbols: boolean;
  ellipsis: boolean;
  emDash: boolean;
  enDash: boolean;
}) {
  return $remark('smart-text-remark', () => () => (tree: Node) => {
    visit(tree, 'text', (node: any) => {
      let text = node.value as string;

      if (enabled.arrows) {
        // Avoid matching inside larger operators (-->, ==>) and require replacing <=> before =>
        text = text
          .replace(/(?<!-)->/g, '\u2192')
          .replace(/<-/g, '\u2190')
          .replace(/<=>/g, '\u21D4')
          .replace(/(?<!=)=>/g, '\u21D2');
      }
      if (enabled.fractions) {
        // Remark scans whole text (unlike input rules); require non-digit boundaries so 1/20 / 11/2 stay numeric
        text = text
          .replace(/(?<!\d)1\/2(?!\d)/g, '\u00BD')
          .replace(/(?<!\d)1\/4(?!\d)/g, '\u00BC')
          .replace(/(?<!\d)3\/4(?!\d)/g, '\u00BE')
          .replace(/(?<!\d)1\/3(?!\d)/g, '\u2153')
          .replace(/(?<!\d)2\/3(?!\d)/g, '\u2154')
          .replace(/(?<!\d)1\/8(?!\d)/g, '\u215B')
          .replace(/(?<!\d)3\/8(?!\d)/g, '\u215C')
          .replace(/(?<!\d)5\/8(?!\d)/g, '\u215D')
          .replace(/(?<!\d)7\/8(?!\d)/g, '\u215E');
      }
      if (enabled.symbols) {
        text = text
          .replace(/\(c\)/g, '\u00A9')
          .replace(/\(r\)/g, '\u00AE')
          .replace(/\(tm\)/g, '\u2122')
          .replace(/\(p\)/g, '\u2117');
      }
      if (enabled.ellipsis) {
        text = text.replace(/\.\.\./g, '\u2026');
      }
      if (enabled.emDash) {
        text = text.replace(/-- /g, '\u2014 ');
      }
      if (enabled.enDash) {
        text = text.replace(/(\d+)-(\d+)/g, '$1\u2013$2');
      }
      node.value = text;
    });
  });
}

/**
 * Creates an input rule for simple text replacement
 */
function createReplacementRule(pattern: string, replacement: string): InputRule {
  // Escape special regex characters in the pattern
  const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Match the pattern at the end of text
  const regex = new RegExp(`${escapedPattern}$`);

  return new InputRule(regex, (state, _match, start, end) => {
    const tr = state.tr;
    tr.replaceWith(start, end, state.schema.text(replacement));

    return tr;
  });
}

/**
 * Creates an input rule for smart quotes
 */
function createSmartQuoteRule(quoteChar: '"' | "'", openQuote: string, closeQuote: string): InputRule {
  return new InputRule(new RegExp(`${quoteChar}$`), (state, _match, start, end) => {
    const { doc, tr } = state;

    // Check the character before the quote
    const beforePos = start - 1;
    const beforeChar = beforePos >= 0 ? doc.textBetween(beforePos, start) : '';

    // Determine if this should be an opening or closing quote
    // Opening quote: after space, newline, or at start of document
    // Closing quote: after letter, number, or punctuation
    const isOpening = beforePos < 0 || beforeChar === '' || /\s/.test(beforeChar) || /[([{]/.test(beforeChar);

    const replacement = isOpening ? openQuote : closeQuote;
    tr.replaceWith(start, end, state.schema.text(replacement));

    return tr;
  });
}

/**
 * Creates an input rule for en dash between numbers
 * Converts: 2-3 to 2 en-dash 3, 10-20 to 10 en-dash 20
 */
function createEnDashRule(): InputRule {
  return new InputRule(/(\d+)-(\d+)$/, (state, match, start, end) => {
    const tr = state.tr;
    const [, before, after] = match;

    // Replace the hyphen with en dash (U+2013)
    const replacement = `${before}\u2013${after}`;
    tr.replaceWith(start, end, state.schema.text(replacement));

    return tr;
  });
}

// Individual composable input rule plugins
const arrowRightRule = $inputRule(() => createReplacementRule('->', '\u2192')); // →
const arrowLeftRule = $inputRule(() => createReplacementRule('<-', '\u2190')); // ←
const arrowBothRule = $inputRule(() => createReplacementRule('<=>', '\u21D4')); // ⇔
const arrowDoubleRightRule = $inputRule(() => createReplacementRule('=>', '\u21D2')); // ⇒

const fractionHalfRule = $inputRule(() => createReplacementRule('1/2', '\u00BD')); // ½
const fractionQuarterRule = $inputRule(() => createReplacementRule('1/4', '\u00BC')); // ¼
const fractionThreeQuartersRule = $inputRule(() => createReplacementRule('3/4', '\u00BE')); // ¾
const fractionThirdRule = $inputRule(() => createReplacementRule('1/3', '\u2153')); // ⅓
const fractionTwoThirdsRule = $inputRule(() => createReplacementRule('2/3', '\u2154')); // ⅔
const fractionEighthRule = $inputRule(() => createReplacementRule('1/8', '\u215B')); // ⅛
const fractionThreeEighthsRule = $inputRule(() => createReplacementRule('3/8', '\u215C')); // ⅜
const fractionFiveEighthsRule = $inputRule(() => createReplacementRule('5/8', '\u215D')); // ⅝
const fractionSevenEighthsRule = $inputRule(() => createReplacementRule('7/8', '\u215E')); // ⅞

const copyrightRule = $inputRule(() => createReplacementRule('(c)', '\u00A9')); // ©
const registeredRule = $inputRule(() => createReplacementRule('(r)', '\u00AE')); // ®
const trademarkRule = $inputRule(() => createReplacementRule('(tm)', '\u2122')); // ™
const soundRecordingRule = $inputRule(() => createReplacementRule('(p)', '\u2117')); // ℗

const ellipsisRule = $inputRule(() => createReplacementRule('...', '\u2026')); // …
const emDashRule = $inputRule(() => createReplacementRule('-- ', '\u2014')); // —
const enDashRule = $inputRule(() => createEnDashRule());

const smartDoubleQuoteRule = $inputRule(() => createSmartQuoteRule('"', '\u201C', '\u201D')); // " and "
const smartSingleQuoteRule = $inputRule(() => createSmartQuoteRule("'", '\u2018', '\u2019')); // ' and '

/**
 * Smart text plugin that provides automatic text replacements
 *
 * Features:
 * - Arrow replacements: -> to right arrow
 * - Fraction replacements: 1/2 to one half, 3/4 to three quarters
 * - Symbol replacements: (c) to copyright, (r) to registered, (tm) to trademark
 * - Ellipsis: ... to horizontal ellipsis
 * - Smart quotes: straight quotes to curly quotes
 * - Em dash: -- to em dash
 * - En dash: 2-3 to 2 en-dash 3
 *
 * @example
 * ```ts
 * crepe.editor.use(arrowRightRule).use(ellipsisRule);
 *
 * // Or use helper with config
 * smartTextPlugin().forEach(plugin => crepe.editor.use(plugin));
 * ```
 */
export function smartTextPlugin(config?: SmartTextConfig): MilkdownPlugin[] {
  const finalConfig = { ...defaultSmartTextConfig, ...config };
  const plugins: MilkdownPlugin[] = [];

  plugins.push(
    ...createRemarkReplacementPlugin({
      arrows: !!finalConfig.arrows,
      fractions: !!finalConfig.fractions,
      symbols: !!finalConfig.symbols,
      ellipsis: !!finalConfig.ellipsis,
      emDash: !!finalConfig.emDash,
      enDash: !!finalConfig.enDash,
    }),
  );

  // Add arrow replacements
  if (finalConfig.arrows) {
    plugins.push(arrowRightRule, arrowLeftRule, arrowBothRule, arrowDoubleRightRule);
  }

  // Add fraction replacements
  if (finalConfig.fractions) {
    plugins.push(
      fractionHalfRule,
      fractionQuarterRule,
      fractionThreeQuartersRule,
      fractionThirdRule,
      fractionTwoThirdsRule,
      fractionEighthRule,
      fractionThreeEighthsRule,
      fractionFiveEighthsRule,
      fractionSevenEighthsRule,
    );
  }

  // Add symbol replacements
  if (finalConfig.symbols) {
    plugins.push(copyrightRule, registeredRule, trademarkRule, soundRecordingRule);
  }

  // Add ellipsis replacement
  if (finalConfig.ellipsis) {
    plugins.push(ellipsisRule);
  }

  // Add em dash replacement
  if (finalConfig.emDash) {
    plugins.push(emDashRule);
  }

  // Add en dash replacement
  if (finalConfig.enDash) {
    plugins.push(enDashRule);
  }

  // Add smart quotes
  if (finalConfig.smartQuotes) {
    plugins.push(smartDoubleQuoteRule, smartSingleQuoteRule);
  }

  return plugins;
}
